import { getVariant } from '@chessbut/engine';
import type { LiveStats } from '@chessbut/shared';
import { useEffect, useMemo, useState } from 'react';
import { BarTable, ColumnChart, DataTable, Funnel, StatTile } from '../components/Charts.tsx';
import { api } from '../lib/api.ts';
import { plural } from '../lib/format.ts';
import { useSession } from '../lib/session.ts';
import { copy } from '../lib/share.ts';
import { openAuth } from '../lib/ui.ts';

interface Daily {
  day: string;
  visitors: number;
  newPlayers: number;
  returning: number;
  played: number;
  finished: number;
  signups: number;
  gamesOnline: number;
  gamesBot: number;
  challenges: number;
  peakOnline: number;
  peakPlaying: number;
}

type Group = { key: string; visitors: number; played: number }[];

interface Dashboard {
  generatedAt: number;
  today: string;
  campaign: { startDate: string; dayNumber: number; featured: { variant: string; day: number } | null };
  live: LiveStats;
  totals: Record<string, number>;
  daily: Daily[];
  variants: { variant: string; games: number; white: number; black: number; draws: number; avg_plies: number; today: number; players: number }[];
  sources: { today: Group; week: Group; month: Group };
  countries: Group;
  devices: Group;
  funnel: { visitors: number; played: number; finished: number; signups: number };
  retention: { cohort: string; size: number; d1: number; d7: number }[];
  events: { name: string; n: number }[];
  reasons: { reason: string; n: number }[];
  pointsToday: { players: number; points: number };
}

const METRICS: { key: keyof Daily; label: string; unit: string }[] = [
  { key: 'visitors', label: 'Visitors', unit: 'visitors' },
  { key: 'played', label: 'Played', unit: 'players' },
  { key: 'newPlayers', label: 'New', unit: 'new players' },
  { key: 'gamesOnline', label: 'Online games', unit: 'games' },
  { key: 'gamesBot', label: 'Bot games', unit: 'games' },
  { key: 'challenges', label: 'Challenges', unit: 'challenges' },
  { key: 'signups', label: 'Signups', unit: 'signups' },
  { key: 'peakOnline', label: 'Peak online', unit: 'players' },
];

const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '–');
const deviceName = (d: string) =>
  d.replace('-ig', ' · Instagram app').replace('-fb', ' · Facebook app').replace('-tiktok', ' · TikTok app').replace(/^ios/, 'iPhone/iPad').replace(/^android/, 'Android').replace(/^desktop/, 'Desktop');

export function AdminPage() {
  const { me } = useSession();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<keyof Daily>('visitors');
  const [sourceRange, setSourceRange] = useState<'today' | 'week' | 'month'>('week');

  useEffect(() => {
    if (!me?.admin) return;
    let cancelled = false;
    const load = () =>
      api<Dashboard>(`/api/admin/dashboard?days=${days}`)
        .then((d) => !cancelled && setData(d))
        .catch((e) => !cancelled && setError((e as Error).message));
    load();
    const id = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [me?.admin, days]);

  const brag = useMemo(() => {
    if (!data) return '';
    const t = data.totals;
    const where = t.countries > 1 ? ` in ${plural(t.countries, 'country', 'countries')}` : '';
    return `Day ${data.campaign.dayNumber} of making chess variants: ${plural(t.gamesOnline + t.gamesBot + t.challenges, 'game')} played by ${plural(t.playersWhoPlayed, 'player')}${where}.`;
  }, [data]);

  if (!me) return <div className="page loading"><span className="spinner big" /></div>;
  if (!me.admin) {
    return (
      <div className="page narrow center">
        <h1>Admins only</h1>
        <p className="muted">Log in with an admin account (set ADMIN_USERNAMES on the server).</p>
        <button className="btn primary big" onClick={() => openAuth('login')}>
          Log in
        </button>
      </div>
    );
  }
  if (!data) return <div className="page loading">{error ? <p>{error}</p> : <span className="spinner big" />}</div>;

  const d = data.daily;
  const today = d[d.length - 1];
  const yesterday = d[d.length - 2];
  const trend = (k: keyof Daily) => d.slice(-14).map((x) => Number(x[k]));
  const m = METRICS.find((x) => x.key === metric)!;
  const featured = data.campaign.featured ? getVariant(data.campaign.featured.variant) : null;

  return (
    <div className="page admin">
      <div className="admin-head">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="muted small">
            Day {data.campaign.dayNumber}
            {featured ? ` · today: ${featured.info.name}` : ''} · campaign started {data.campaign.startDate} · updated{' '}
            {new Date(data.generatedAt).toLocaleTimeString()}
          </p>
        </div>
        <div className="chips">
          {[7, 30, 90].map((n) => (
            <button key={n} className={`chip ${days === n ? 'is-selected' : ''}`} onClick={() => setDays(n)}>
              {n} days
            </button>
          ))}
        </div>
      </div>

      <section className="card brag">
        <span>{brag}</span>
        <button className="btn small" onClick={() => copy(brag, 'Copied for your caption')}>
          Copy
        </button>
      </section>

      <div className="kpi-row">
        <div className="stat-tile live">
          <span className="stat-label">
            <span className="live-dot" /> Live now
          </span>
          <b className="stat-value">{data.live.online}</b>
          <span className="stat-foot">
            {data.live.playing} playing · {Object.values(data.live.searching).reduce((a, b) => a + b, 0)} searching
          </span>
        </div>
        <StatTile label="Visitors today" value={today.visitors} previous={yesterday?.visitors} trend={trend('visitors')} />
        <StatTile label="New players" value={today.newPlayers} previous={yesterday?.newPlayers} trend={trend('newPlayers')} />
        <StatTile label="Played today" value={today.played} previous={yesterday?.played} trend={trend('played')} />
        <StatTile label="Online games" value={today.gamesOnline} previous={yesterday?.gamesOnline} trend={trend('gamesOnline')} />
        <StatTile label="Bot games" value={today.gamesBot} previous={yesterday?.gamesBot} trend={trend('gamesBot')} />
        <StatTile label="Challenges" value={today.challenges} previous={yesterday?.challenges} trend={trend('challenges')} />
        <StatTile label="Signups" value={today.signups} previous={yesterday?.signups} trend={trend('signups')} />
        <StatTile label="Peak online" value={today.peakOnline} previous={yesterday?.peakOnline} trend={trend('peakOnline')} />
      </div>

      <section className="card">
        <div className="card-head">
          <h2>{m.label} per day</h2>
          <div className="chips">
            {METRICS.map((x) => (
              <button key={x.key} className={`chip small ${metric === x.key ? 'is-selected' : ''}`} onClick={() => setMetric(x.key)}>
                {x.label}
              </button>
            ))}
          </div>
        </div>
        <ColumnChart data={d.map((x) => ({ label: x.day, value: Number(x[metric]) }))} unit={m.unit} ariaLabel={`${m.label} per day`} />
      </section>

      <div className="admin-grid">
        <section className="card">
          <h2>Funnel · last 7 days</h2>
          <Funnel
            stages={[
              { label: 'Visited', value: data.funnel.visitors },
              { label: 'Started a game', value: data.funnel.played },
              { label: 'Finished a game', value: data.funnel.finished },
              { label: 'Created account', value: data.funnel.signups },
            ]}
          />
        </section>

        <section className="card">
          <div className="card-head">
            <h2>Sources</h2>
            <div className="chips">
              {(['today', 'week', 'month'] as const).map((r) => (
                <button key={r} className={`chip small ${sourceRange === r ? 'is-selected' : ''}`} onClick={() => setSourceRange(r)}>
                  {r === 'today' ? 'Today' : r === 'week' ? '7d' : `${days}d`}
                </button>
              ))}
            </div>
          </div>
          <BarTable
            head={['Source (?ref=)', 'Visitors', 'Played']}
            rows={data.sources[sourceRange].map((s) => ({ key: s.key, value: s.visitors, extra: `${s.played} · ${pct(s.played, s.visitors)}` }))}
          />
          <p className="muted small">Tag reel links like chessbut.app/1?ref=reel1 to see which reel brought people in.</p>
        </section>

        <section className="card">
          <h2>Countries · 7 days</h2>
          <BarTable head={['Country', 'Visitors', 'Played']} rows={data.countries.map((s) => ({ key: s.key, value: s.visitors, extra: s.played }))} />
        </section>

        <section className="card">
          <h2>Devices · 7 days</h2>
          <BarTable head={['Device', 'Visitors', 'Played']} rows={data.devices.map((s) => ({ key: deviceName(s.key), value: s.visitors, extra: s.played }))} />
        </section>

        <section className="card wide">
          <h2>Retention</h2>
          <DataTable
            head={['First visit', 'New players', 'Back next day', 'Back after 7 days']}
            rows={data.retention.map((r) => [r.cohort, r.size, `${r.d1} · ${pct(r.d1, r.size)}`, `${r.d7} · ${pct(r.d7, r.size)}`])}
          />
        </section>

        <section className="card wide">
          <h2>Variants · all time</h2>
          <DataTable
            head={['Variant', 'Games', 'Today', 'Players', 'Avg moves', 'White / Black / Draw']}
            rows={data.variants.map((v) => [
              getVariant(v.variant)?.info.name ?? v.variant,
              v.games,
              v.today,
              v.players,
              Math.round(v.avg_plies / 2),
              `${pct(v.white, v.games)} / ${pct(v.black, v.games)} / ${pct(v.draws, v.games)}`,
            ])}
          />
        </section>

        <section className="card">
          <h2>Totals</h2>
          <DataTable
            head={['Metric', 'Value']}
            rows={[
              ['Players (sessions)', data.totals.players],
              ['Players who played', data.totals.playersWhoPlayed],
              ['Accounts', data.totals.accounts],
              ['Online games', data.totals.gamesOnline],
              ['Bot games', data.totals.gamesBot],
              ['Daily challenges', data.totals.challenges],
              ['Moves played', data.totals.moves],
              ['Countries', data.totals.countries],
              ['Peak online', data.totals.peakOnline],
              ['Points scored today', `${data.pointsToday.points} by ${data.pointsToday.players}`],
            ]}
          />
        </section>

        <section className="card">
          <h2>Events · 7 days</h2>
          <BarTable head={['Event', 'Count']} rows={data.events.map((e) => ({ key: e.name, value: e.n }))} />
        </section>

        <IdeasCard />

        <section className="card">
          <h2>How games end</h2>
          <BarTable head={['Reason', 'Games']} rows={data.reasons.map((r) => ({ key: r.reason, value: r.n }))} />
        </section>
      </div>
    </div>
  );
}

function IdeasCard() {
  const [ideas, setIdeas] = useState<{ id: number; at: number; text: string; name: string | null; username: string | null }[] | null>(null);
  useEffect(() => {
    api<{ ideas: NonNullable<typeof ideas> }>('/api/admin/ideas')
      .then((r) => setIdeas(r.ideas))
      .catch(() => setIdeas([]));
  }, []);
  return (
    <section className="card wide">
      <h2>Variant ideas from players</h2>
      {!ideas ? (
        <p className="muted small">Loading…</p>
      ) : ideas.length ? (
        <DataTable
          head={['When', 'chess, but…', 'From']}
          rows={ideas.slice(0, 50).map((i) => [new Date(i.at).toLocaleDateString(), i.text, i.username ?? i.name ?? 'guest'])}
        />
      ) : (
        <p className="muted small">None yet. Link /ideas in your bio or a pinned comment.</p>
      )}
    </section>
  );
}
